import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Mail, MapPin, Phone } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white">
      <div className="container mx-auto px-4 py-12">
        {/* Top Section: Logo + Checkatrade Widget */}
        <div className="flex flex-col md:flex-row items-center justify-between mb-12 pb-8 border-b border-slate-800">
          {/* Logo with white background */}
          <div className="mb-6 md:mb-0">
            <Link to="/" className="flex items-center">
              <div className="bg-white rounded-2xl p-4 shadow-lg">
                <img 
                  src="/images/emergent/logo.png" 
                  alt="Paint It Better" 
                  className="h-24 w-auto"
                />
              </div>
            </Link>
          </div>

          {/* Checkatrade Widget - Now Clickable */}
          <div className="text-center md:text-right">
            <a 
              href="https://www.checkatrade.com/trades/PaintItBetterLtd" 
              target="_blank" 
              rel="noopener"
              className="block group"
            >
              <h3 className="text-lg font-semibold mb-3 group-hover:text-cyan-400 transition-colors">See Our Customer Reviews →</h3>
              <p className="text-sm text-slate-400 group-hover:text-slate-300 transition-colors">Trusted by hundreds of satisfied customers on Checkatrade</p>
            </a>
          </div>
        </div>

        {/* Bottom Section: Quick Links, Services, Contact */}
        <div className="grid md:grid-cols-4 gap-8">
          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              {['Home', 'Services', 'Portfolio', 'About', 'Contact'].map((item) => (
                <li key={item}>
                  <Link 
                    to={`/${item.toLowerCase() === 'home' ? '' : item.toLowerCase()}`}
                    className="text-slate-400 hover:text-cyan-400 transition-colors text-sm"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Resources</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/faq" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/painters-decorators-ashford" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm">
                  Painters in Ashford
                </Link>
              </li>
              <li>
                <Link to="/painters-decorators-maidstone" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm">
                  Painters in Maidstone
                </Link>
              </li>
              <li>
                <Link to="/painters-decorators-canterbury" className="text-slate-400 hover:text-cyan-400 transition-colors text-sm">
                  Painters in Canterbury
                </Link>
              </li>
              <li>
                <a 
                  href="https://www.checkatrade.com/trades/PaintItBetterLtd" 
                  target="_blank" 
                  rel="noopener"
                  className="text-slate-400 hover:text-cyan-400 transition-colors text-sm"
                >
                  Checkatrade Reviews
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Our Services</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/services#interior-painting" className="text-slate-400 hover:text-cyan-400 transition-colors">Interior Painting</Link></li>
              <li><Link to="/services#exterior-painting" className="text-slate-400 hover:text-cyan-400 transition-colors">Exterior Painting</Link></li>
              <li><Link to="/services#landlords-rental-properties" className="text-slate-400 hover:text-cyan-400 transition-colors">Landlords & Rentals</Link></li>
              <li><Link to="/services#offices-shops" className="text-slate-400 hover:text-cyan-400 transition-colors">Offices & Shops</Link></li>
              <li><Link to="/services#period-properties" className="text-slate-400 hover:text-cyan-400 transition-colors">Period Properties</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-2 text-sm">
                <MapPin className="h-5 w-5 text-cyan-500 flex-shrink-0" />
                <span className="text-slate-400">
                  Wye, Ashford TN25 5AS<br />
                  Kent, UK
                </span>
              </li>
              <li className="flex items-center space-x-2 text-sm">
                <Mail className="h-5 w-5 text-cyan-500" />
                <a href="mailto:hello@paint-it-better.com" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  hello@paint-it-better.com
                </a>
              </li>
              <li className="flex items-center space-x-2 text-sm">
                <Facebook className="h-5 w-5 text-cyan-500" />
                <a href="http://facebook.com/paint.it.better.uk" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-cyan-400 transition-colors">
                  Follow us on Facebook
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-slate-400">
          <p>&copy; {new Date().getFullYear()} Paint It Better Ltd. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/privacy" className="hover:text-cyan-400 transition-colors">Privacy Policy</Link>
          </div>
        </div>

        {/* Corporate Information */}
        <div className="mt-6 pt-6 border-t border-slate-800 text-center text-xs text-slate-500">
          <p>
            Paint It Better Ltd, incorporated in England (CRN 15150333)
          </p>
          <p className="mt-1">
            The Business Terrace C/O Silverstone Law, The Business Terrace, Maidstone House, Maidstone, Kent, United Kingdom, ME15 6JQ
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;